import { Link } from 'react-router-dom'
import clsx from 'clsx'
import type { NotificationCategory, NotificationDto } from '@/types/notifications'
import { groupForNotification, relativeFromDays } from '@/lib/notification-deck'
import { IconButton } from '@/components/carbon/IconButton'
import { IconClose } from '@/components/carbon/icons'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { Tag } from '@/components/carbon/Tag'
import type { SeverityLevel } from '@/components/carbon/types'

/** The source, as a short helper label beside the severity. */
const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  waiver: 'Best Practice waiver',
  'app-credential': 'App credential',
  'app-review': 'App review',
}

/**
 * A row's severity by shape and word (DESIGN.md, the Shape-First Severity
 * Rule): the server's critical tier takes the Coral octagon; a warning
 * inside the two-week window takes the triangle, "Due soon"; a later one
 * the square, "Upcoming", the same words as the feed's sections.
 */
function notificationSeverity(item: NotificationDto): { level: SeverityLevel; word: string } {
  if (item.severity === 'critical') return { level: 'crit', word: 'Critical' }
  return groupForNotification(item) === 'due-soon' ? { level: 'high', word: 'Due soon' } : { level: 'low', word: 'Upcoming' }
}

interface NotificationItemProps {
  item: NotificationDto
  onDismiss: (key: string) => void
  /**
   *  - `compact`     — dropdown rows. Tight padding, no body line.
   *  - `comfortable` — `/notifications` page rows. Larger padding, body line shown.
   */
  size?: 'compact' | 'comfortable'
}

/**
 * One notification, as a Carbon structured-list row: the severity glyph and
 * its word, the source, and a "New" high-contrast tag while unread, all in
 * helper text; the server-composed title at 600; the body on the full page;
 * and the relative time with a dismiss button on the right. Unread is a
 * labelled tag, never a bare dot (the Neutral Bad State Rule).
 *
 * The title is the row's link and its hit area stretches over the whole row,
 * so the user can click anywhere; the dismiss button sits above that area as
 * a sibling, never nested inside the link.
 */
export default function NotificationItem({
  item,
  onDismiss,
  size = 'compact',
}: NotificationItemProps) {
  const severity = notificationSeverity(item)
  const comfortable = size === 'comfortable'

  return (
    <div
      className={clsx(
        'group relative flex items-start gap-4 border-b border-line-subtle last:border-b-0',
        'transition-colors duration-110 ease-carbon hover:bg-layer-hover',
        comfortable ? 'py-4 pl-5 pr-3' : 'py-3 pl-4 pr-2',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-helper text-text-helper">
          <SeverityGlyph level={severity.level} size={12}>
            {severity.word}
          </SeverityGlyph>
          <span>{CATEGORY_LABEL[item.category]}</span>
          {item.isRead ? null : (
            <Tag kind="high-contrast" size="sm">
              New
            </Tag>
          )}
        </div>
        <Link
          to={item.href}
          className={clsx(
            'mt-1 block font-semibold text-text-primary after:absolute after:inset-0',
            comfortable ? 'text-body-lg' : 'text-body leading-[1.29]',
          )}
        >
          {item.title}
        </Link>
        {comfortable && item.body ? <p className="m-0 mt-1.5 text-body text-text-secondary">{item.body}</p> : null}
        {comfortable && item.subjectName ? (
          <p className="m-0 mt-1.5 text-helper text-text-helper">{item.subjectName}</p>
        ) : null}
      </div>

      <div className="-my-1.5 flex flex-none items-center gap-1">
        <span className="text-helper tabular-nums text-text-helper">{relativeFromDays(item.daysUntil)}</span>
        <IconButton
          label={`Dismiss ${item.title}`}
          icon={<IconClose />}
          size={32}
          onClick={() => onDismiss(item.key)}
          className="relative z-10 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 max-c-lg:opacity-100"
        />
      </div>
    </div>
  )
}
